import classNames from 'classnames'
import React from 'react'
import { LineChart as RechartsLineChart, ResponsiveContainer } from 'recharts'

import { ChartContext } from '../chart/ChartWrapper'
import { Line } from '../chart/Line'
import { LineDot } from '../chart/LineDot'
import { Tooltip } from '../chart/Tooltip'
import { YAxis } from '../chart/YAxis'
import { ChartItemColorType } from '../chart/constants'
import ChartTooltip from './ChartTooltip'
import {
  BAR_GAP,
  BAR_WIDTH,
  CHART_HEIGHT,
  EXTRA_WIDTH,
  LABELS_WIDTH,
  LINE_CHART_LABELS_MARGIN,
  LINE_CHART_MARGINS,
} from './constants'
import { ChartProps, LineChartTooltipSort } from './types'
import { getChartDataItemKey, tickFormatter } from './utils'

interface Props extends ChartProps {
  tooltipSort?: keyof typeof LineChartTooltipSort
}

export default function LineChart({
  chartData,
  visibleAreas,
  columnsCount,
  barWidth = BAR_WIDTH,
  tooltipSort,
  showTotal,
  isFullWidth,
}: Props) {
  const chartWidth = columnsCount * barWidth + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH
  const ChartWrapper = isFullWidth ? ResponsiveContainer : React.Fragment

  return (
    <ChartContext {...(isFullWidth ? { className: 'w-full', style: { height: CHART_HEIGHT } } : {})}>
      <ChartWrapper>
        <RechartsLineChart
          width={isFullWidth ? barWidth * chartData.length : chartWidth}
          height={CHART_HEIGHT}
          data={chartData}
          margin={isFullWidth ? { ...LINE_CHART_MARGINS, left: barWidth / 2 } : LINE_CHART_MARGINS}
        >
          <YAxis
            orientation="right"
            textAnchor="end"
            tickFormatter={tickFormatter}
            interval="preserveStartEnd"
            tickMargin={LINE_CHART_LABELS_MARGIN}
            width={LABELS_WIDTH}
          />
          <Tooltip content={<ChartTooltip payloadSort={tooltipSort} />} />
          {showTotal && (
            <Line
              name="Total"
              dot={<LineDot />}
              activeDot={<LineDot isActive color="lightGray" />}
              dataKey="total"
              color="lightGray"
            />
          )}
          {visibleAreas.map((area) => {
            const itemKey = getChartDataItemKey(area)
            return (
              <Line
                key={itemKey}
                name={area.label}
                dataKey={itemKey}
                color={area.color as ChartItemColorType}
                stroke={area.color}
                strokeWidth={area.isHighlighted ? 2 : 1}
                dot={<LineDot className={classNames({ 'stroke-2': area.isHighlighted })} />}
                activeDot={
                  <LineDot
                    isActive
                    color={area.color as ChartItemColorType}
                    className={classNames({ 'stroke-[3]': area.isHighlighted, 'stroke-0': !area.isHighlighted })}
                  />
                }
              />
            )
          })}
        </RechartsLineChart>
      </ChartWrapper>
    </ChartContext>
  )
}
